'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { BaseVideoPlayer } from '@/app/components/shared/VideoPlayer/BaseVideoPlayer';
import { Button } from '@/app/components/ui/button';
import { cn } from '@/app/utils';
import { USE_CASE_CATEGORIES, USE_CASE_VIDEOS, type UseCaseCategory } from '../_constants/media';

// 品类 tab 暂不上线：置 false 时隐藏 tab 行并展示全部案例。待品类归类确认后改回 true。
const SHOW_CATEGORY_TABS = false;

export const UseCaseSection: React.FC = () => {
  const t = useTranslations('vidu-stream');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [activeCategory, setActiveCategory] = useState<UseCaseCategory>('all');

  const visibleVideos = useMemo(
    () =>
      activeCategory === 'all' ? USE_CASE_VIDEOS : USE_CASE_VIDEOS.filter((video) => video.category === activeCategory),
    [activeCategory],
  );

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  const scrollToLeft = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: -el.clientWidth, behavior: 'smooth' });
  }, []);

  const scrollToRight = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  // 切换品类后列表变化，滚回起点并重算箭头/蒙版状态。
  useEffect(() => {
    scrollRef.current?.scrollTo({ left: 0 });
    updateScrollState();
  }, [activeCategory, updateScrollState]);

  return (
    <section className="mx-auto w-full max-w-[1440px] px-8 pt-[104px] pb-16 sm:px-4 sm:pt-20">
      <h2
        className={cn(
          'text-center font-medium text-[40px] text-white leading-[1.4] md:text-[36px]',
          SHOW_CATEGORY_TABS ? 'mb-8 md:mb-6' : 'mb-16 md:mb-8',
        )}
      >
        {t('useCases.title')}
      </h2>
      {/* 品类 tab：底部整条细横线，选中项用更粗横线高亮，文字不加粗。移动端可横向滚动 */}
      {SHOW_CATEGORY_TABS && (
        <div className="hide-scrollbar mb-12 flex justify-center gap-2 overflow-x-auto border-white/10 border-b sm:mb-8 sm:justify-start sm:px-1">
          {USE_CASE_CATEGORIES.map((category) => {
            const isActive = activeCategory === category;
            return (
              <button
                key={category}
                type="button"
                onClick={() => setActiveCategory(category)}
                className={cn(
                  '-mb-px shrink-0 whitespace-nowrap border-b-2 px-6 pb-3 text-base transition-colors sm:px-5 sm:text-sm',
                  isActive ? 'border-white text-white' : 'border-transparent text-white/60 hover:text-white',
                )}
                aria-current={isActive ? 'true' : undefined}
              >
                {t(`useCases.tabs.${category}`)}
              </button>
            );
          })}
        </div>
      )}
      <div className="-mx-8 sm:-mx-4 relative">
        <div ref={scrollRef} className="hide-scrollbar flex gap-5 overflow-x-auto px-8 sm:gap-4 sm:px-4">
          {visibleVideos.map(({ src, poster }) => (
            <div
              key={src}
              className="group relative w-[calc((100%-90px)/4.5)] shrink-0 overflow-hidden rounded-16 bg-black sm:w-[calc((100%-24px)/1.5)]"
              style={{ aspectRatio: '9/16' }}
            >
              <BaseVideoPlayer
                src={src}
                poster={poster}
                playOnHover
                hoverTogglesControls
                hasAudio
                defaultVolume={0.6}
                isLazy
                className="object-cover"
              />
              {/* 自定义封面：默认展示，hover 时淡出露出视频。含 20% 黑蒙版。 */}
              <div className="pointer-events-none absolute inset-0 transition-opacity duration-300 group-hover:opacity-0">
                <img src={poster} alt="" loading="lazy" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-black/30" />
              </div>
            </div>
          ))}
        </div>
        {/* 左侧被遮挡时的渐变黑蒙版，提示可向左滑 */}
        <div
          className={cn(
            'pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-black/60 to-transparent transition-opacity duration-300 sm:w-16',
            canScrollLeft ? 'opacity-100' : 'opacity-0',
          )}
        />
        {/* 右侧被遮挡时的渐变黑蒙版，提示可向右滑 */}
        <div
          className={cn(
            'pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-black/60 to-transparent transition-opacity duration-300 sm:w-16',
            canScrollRight ? 'opacity-100' : 'opacity-0',
          )}
        />
        {/* 左箭头按钮 */}
        {canScrollLeft && (
          <Button
            className="group -translate-y-1/2 absolute top-1/2 left-4 z-10 flex size-10 items-center justify-center rounded-full border border-white/20 bg-black/40 p-0 backdrop-blur-sm transition hover:bg-black/60 sm:left-2 sm:size-8"
            onClick={scrollToLeft}
          >
            <BaseLineLeft className="size-5 text-white sm:size-4" />
          </Button>
        )}
        {/* 右箭头按钮 */}
        {canScrollRight && (
          <Button
            className="group -translate-y-1/2 absolute top-1/2 right-4 z-10 flex size-10 items-center justify-center rounded-full border border-white/20 bg-black/40 p-0 backdrop-blur-sm transition hover:bg-black/60 sm:right-2 sm:size-8"
            onClick={scrollToRight}
          >
            <BaseLineRight className="size-5 text-white sm:size-4" />
          </Button>
        )}
      </div>
    </section>
  );
};
